import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { unwrapResult } from '@reduxjs/toolkit'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { apiStatus } from 'shared/constants/AppEnum'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
    selectStaffCalendarRows,
    selectStaffCalendarStatus,
    staffCalendarListThunk,
} from 'store/reducers/temple/staffCalendarSlice'
import { staffAssignmentReassignOccurrenceThunk } from 'store/reducers/temple/staffAssignmentSlice'
import { selectCurrentTempleStaffProfiles, staffProfileListThunk } from 'store/reducers/temple/staffProfileSlice'
import { StaffCalendarFilterType, StaffCalendarRowType } from 'types/api/temple'
import { extractError } from 'utils/api'
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions'
import useRestricter from 'hooks/useRestricter'
import usePermissionCheck from 'hooks/usePermissionCheck'

const toDateInput = (date: Date) => date.toISOString().slice(0, 10)

const today = new Date()
const nextWeek = new Date()
nextWeek.setDate(today.getDate() + 7)

const initialFilters: StaffCalendarFilterType = {
    dateFrom: toDateInput(today),
    dateTo: toDateInput(nextWeek),
    staff: '',
    role: '',
    status: '',
}

const label = (value: string) => value.split('_').map(item => item.charAt(0).toUpperCase() + item.slice(1)).join(' ')

const StaffCalendar = () => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const rows = useAppSelector(selectStaffCalendarRows)
    const api = useAppSelector(selectStaffCalendarStatus)
    const profiles = useAppSelector(selectCurrentTempleStaffProfiles)
    const [filters, setFilters] = useState<StaffCalendarFilterType>(initialFilters)
    const [reassignRow, setReassignRow] = useState<StaffCalendarRowType | null>(null)
    const [replacementStaff, setReplacementStaff] = useState<string | number>('')
    const hasViewPermission = useRestricter(PERMISSIONS.VIEW, RESTRICTED_PATHS.STAFF_CALENDAR)
    const assignmentPermissions = usePermissionCheck(RESTRICTED_PATHS.STAFF_ASSIGNMENTS)
    const activePriests = profiles.filter(profile => profile.roleType === 'priest' && profile.isActive)

    const handleReassign = () => {
        if (!reassignRow || !replacementStaff) return
        dispatch(staffAssignmentReassignOccurrenceThunk({
            occurrenceId: reassignRow.sourceId,
            staffProfile: replacementStaff,
        }))
            .then(unwrapResult)
            .then(() => {
                enqueueSnackbar('Assignment reassigned', { variant: 'success' })
                setReassignRow(null)
                setReplacementStaff('')
                dispatch(staffCalendarListThunk(filters))
            })
            .catch(() => enqueueSnackbar('Unable to reassign assignment', { variant: 'error' }))
    }

    useEffect(() => {
        dispatch(staffProfileListThunk({}))
        dispatch(staffCalendarListThunk(initialFilters))
    }, [dispatch])

    if (!hasViewPermission) return null

    return (
        <Paper>
            <Box p={5}>
                <Typography variant='h6'>Staff Calendar</Typography>
                <Box mt={4}>
                    <Grid container spacing={4} alignItems='center'>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                variant='outlined'
                                label='From'
                                type='date'
                                value={filters.dateFrom}
                                InputLabelProps={{ shrink: true }}
                                onChange={event => setFilters(prev => ({ ...prev, dateFrom: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                variant='outlined'
                                label='To'
                                type='date'
                                value={filters.dateTo}
                                InputLabelProps={{ shrink: true }}
                                onChange={event => setFilters(prev => ({ ...prev, dateTo: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth variant='outlined'>
                                <InputLabel id='staff-calendar-profile-label'>Staff/Priest</InputLabel>
                                <Select
                                    labelId='staff-calendar-profile-label'
                                    label='Staff/Priest'
                                    value={filters.staff ?? ''}
                                    onChange={event => setFilters(prev => ({ ...prev, staff: event.target.value as string }))}
                                >
                                    <MenuItem value=''>All</MenuItem>
                                    {profiles.map(profile => (
                                        <MenuItem key={profile.id} value={profile.id}>{profile.fullName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth variant='outlined'>
                                <InputLabel id='staff-calendar-role-label'>Role</InputLabel>
                                <Select
                                    labelId='staff-calendar-role-label'
                                    label='Role'
                                    value={filters.role ?? ''}
                                    onChange={event => setFilters(prev => ({ ...prev, role: event.target.value as string }))}
                                >
                                    <MenuItem value=''>All</MenuItem>
                                    <MenuItem value='administrator'>Administrator</MenuItem>
                                    <MenuItem value='manager'>Manager</MenuItem>
                                    <MenuItem value='staff'>Staff</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth variant='outlined'>
                                <InputLabel id='staff-calendar-status-label'>Status</InputLabel>
                                <Select
                                    labelId='staff-calendar-status-label'
                                    label='Status'
                                    value={filters.status ?? ''}
                                    onChange={event => setFilters(prev => ({ ...prev, status: event.target.value as StaffCalendarFilterType['status'] }))}
                                >
                                    <MenuItem value=''>All</MenuItem>
                                    <MenuItem value='assignment'>Assignments</MenuItem>
                                    <MenuItem value='time_off'>Time Off</MenuItem>
                                    <MenuItem value='scheduled'>Scheduled</MenuItem>
                                    <MenuItem value='cancelled'>Cancelled</MenuItem>
                                    <MenuItem value='recurrence'>Recurrence</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <Button color='primary' variant='contained' onClick={() => dispatch(staffCalendarListThunk(filters))}>
                                Apply
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
                {api.status === apiStatus.FAILED && (
                    <Box mt={4}>
                        <Alert severity='error'>{extractError(api.error)}</Alert>
                    </Box>
                )}
            </Box>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Staff/Priest</TableCell>
                        <TableCell>Work</TableCell>
                        <TableCell>Venue</TableCell>
                        <TableCell>Status</TableCell>
                        {assignmentPermissions.includes(PERMISSIONS.REASSIGN) && <TableCell align='right'>Actions</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={`${row.sourceType}-${row.sourceId}`}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{row.startTime} - {row.endTime}</TableCell>
                            <TableCell>{row.staffName}</TableCell>
                            <TableCell>
                                <Chip
                                    size='small'
                                    color={row.sourceType === 'assignment' ? 'primary' : 'default'}
                                    label={label(row.sourceType)}
                                    style={{ marginRight: 8 }}
                                />
                                {row.label}
                            </TableCell>
                            <TableCell>{row.venueName || '-'}</TableCell>
                            <TableCell>{label(row.status)}</TableCell>
                            {assignmentPermissions.includes(PERMISSIONS.REASSIGN) && (
                                <TableCell align='right'>
                                    {row.sourceType === 'assignment' && (
                                        <Button
                                            size='small'
                                            color='primary'
                                            variant='outlined'
                                            onClick={() => {
                                                setReassignRow(row)
                                                setReplacementStaff('')
                                            }}
                                        >
                                            Reassign
                                        </Button>
                                    )}
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={assignmentPermissions.includes(PERMISSIONS.REASSIGN) ? 7 : 6}>No staff calendar entries found</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <Dialog open={Boolean(reassignRow)} fullWidth maxWidth='sm'>
                <DialogTitle>Reassign Assignment</DialogTitle>
                <DialogContent>
                    <Box mb={4}>
                        <Typography variant='body2'>
                            {reassignRow?.date} {reassignRow?.startTime} - {reassignRow?.endTime}: {reassignRow?.label}
                        </Typography>
                    </Box>
                    <FormControl fullWidth variant='outlined'>
                        <InputLabel id='staff-reassign-profile-label'>Replacement Priest</InputLabel>
                        <Select
                            labelId='staff-reassign-profile-label'
                            label='Replacement Priest'
                            value={replacementStaff}
                            onChange={event => setReplacementStaff(event.target.value as string)}
                        >
                            {activePriests
                                .filter(profile => profile.id !== reassignRow?.staffProfile)
                                .map(profile => (
                                    <MenuItem key={profile.id} value={profile.id}>{profile.fullName}</MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReassignRow(null)} color='primary' variant='outlined'>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReassign}
                        color='primary'
                        variant='contained'
                        disabled={!replacementStaff}
                    >
                        Reassign
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default StaffCalendar
